import { createCodedError } from '~/common/errors/CodedError';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import {
  EsignatureEnvelope,
  EsignatureEnvelopeStatus,
} from '~/features/ticket/types/esignature';
import { JsonApiErrorResponse } from '~/redux/api/ApiTypes';
import { testIsAxiosError } from '~/redux/api/utils';
import { everest } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

type Request = {
  ticketId: Uuid;
};

type ServerResponse = {
  data: {
    type: string;
    id: string;
    attributes: {
      status: EsignatureEnvelopeStatus;
    };
  };
};

export const getEnvelope = async ({
  ticketId,
}: Request): Promise<Nullable<EsignatureEnvelope>> => {
  try {
    const response: ServerResponse = await everest.get(
      `tickets/${ticketId}/packages/status`,
    );

    return mapResponse(response);
  } catch (error) {
    if (
      testIsAxiosError(error) &&
      error.response?.status === HttpStatusCodeType.NotFound
    ) {
      // if the ticket has no envelope, server returns 404
      return null;
    }

    if (testIsAxiosError<JsonApiErrorResponse>(error) && error.response) {
      const errorResponse = error.response.data.errors[0];
      throw createCodedError({
        message: errorResponse.detail ?? errorResponse.title,
        statusCode: errorResponse.status,
        title: errorResponse.title,
      });
    }

    throw error;
  }
};

const mapResponse = (
  serverResponse: ServerResponse,
): Nullable<EsignatureEnvelope> => {
  if (
    serverResponse.data === null ||
    serverResponse.data.attributes.status === null
  ) {
    return null;
  }

  return {
    status: serverResponse.data.attributes.status,
  };
};
